import { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles as faSparklesSolid } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { Button, UnstyledButton } from '@mantine/core';

import { emptyArray } from 'ui-components/default-props/empty';
import { useToggle } from 'ui-components/hooks/use-toggle';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { Features } from '../../../../../constants/features';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { useIsSellerFeatureEnabled } from '../../../../../hooks/use-is-seller-feature-enabled';
import { useSellerSetting } from '../../../../../hooks/use-seller-setting';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import Global from '../../../../../store/global-selectors';
import { usePackingCalculation } from '../../../../../packing/hooks/use-packing-calculation';
import type { JobResultResponse, PackingError } from '../../../../../packing/services/packing-service';
import {
  PackingRecommendationFailureReason,
  PackingRecommendationStatus,
  getTerminalPackingRecommendationStatus,
} from '../../../../../packing/services/packing-recommendation-status';
import type { PackingRecommendationStatusFields } from '../../../../../packing/services/packing-recommendation-status';
import { validateAndTransform, DEFAULT_OPTIMIZATION_FACTOR, ValidationErrorType } from '../../../../../packing/utils/data-transformer';
import type { BlockingValidationErrorType } from '../../../../../packing/utils/data-transformer';
import type { OrderItem, Package } from '../../../../../components/modals/multi-package-modal-dnd/types';

import {
  trackPackingRecommendationFailed,
  trackPackingRecommendationRejected,
  trackPackingRecommendationRequested,
  trackPackingRecommendationRerunClicked,
  trackPackingRecommendationRetryClicked,
  trackPackingRecommendationShown,
} from '../../../../../packing/services/packing-recommendations-analytics';

import { PackingRecommendationOverrideReasonModal } from '../../../../../packing/enforcement/packing-recommendation-override-reason-modal';
import { PackingRecommendationWarningModal } from '../../../../../packing/enforcement/packing-recommendation-warning-modal';
import { usePackingRecommendationGuard } from '../../../../../packing/enforcement/use-packing-recommendation-guard';
import { PackingResultCard } from './components/packing-result-card/packing-result-card';
import { PackingValidationWarning } from './components/packing-validation-warning/packing-validation-warning';
import { PackingPackageList } from './components/packing-package-list/packing-package-list';
import { PackingInlineBanner } from './components/packing-inline-banner/packing-inline-banner';
import { useOrderData } from './hooks/use-order-data';
import { useApplyRecommendation } from './hooks/use-apply-recommendation';
import { getPackagesCustomSettingsPath } from '../../../../../routes/constants';
import { messages } from './messages';
import cardStyle from './packing-card.module.css';
import {
  cacheResult,
  clearCachedResult,
  clearCalculating,
  clearError,
  getActiveJob,
  getAppliedState,
  getCachedResult,
  getPersistedError,
  hasError,
  isCurrentlyCalculating,
  isMultiPackageDrawerOpen,
  markError,
  setActiveJob,
  triggerCalculation,
  useOnResultCleared,
  useOnResultReady,
  useOnTriggerCalculation,
} from './use-packing-applied';
import { CONDENSED_BUTTON_FONT_FIX } from './panel-button-style';

const STATUS_TITLE_MESSAGES = {
  [PackingRecommendationStatus.Dismissed]: messages.statusCardDismissedTitle,
  [PackingRecommendationStatus.Failed]: messages.statusCardFailedTitle,
  [PackingRecommendationStatus.Stale]: messages.panelTitle,
} as const;

const FAILURE_REASON_MESSAGES: Partial<Record<PackingRecommendationFailureReason, (typeof messages)[keyof typeof messages]>> = {
  [PackingRecommendationFailureReason.NoItems]: messages.failureReasonNoItems,
  [PackingRecommendationFailureReason.NoValidPackage]: messages.failureReasonNoValidPackage,
  [PackingRecommendationFailureReason.OrderAlreadyShipped]: messages.failureReasonOrderAlreadyShipped,
  [PackingRecommendationFailureReason.PackingAiServiceError]: messages.failureReasonPackingAiServiceError,
  [PackingRecommendationFailureReason.Timeout]: messages.failureReasonTimeout,
  [PackingRecommendationFailureReason.Unknown]: messages.failureReasonUnknown,
};

const BLOCKING_VALIDATION_PRIORITY: BlockingValidationErrorType[] = [
  ValidationErrorType.NO_PACKAGES,
  ValidationErrorType.MISSING_DIMENSIONS,
  ValidationErrorType.MISSING_WEIGHT,
  ValidationErrorType.MISSING_ITEM_NAME,
];

type FulfillmentPlanPackingRecommendation = PackingRecommendationStatusFields;

type PackingRecommendProps = {
  fulfillmentPlanId: string;
  hideButton?: boolean;
  currentPackages?: Package[];
};

export const PackingRecommend = ({
  fulfillmentPlanId,
  hideButton = false,
  currentPackages: currentPackagesOverride,
}: PackingRecommendProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const isPackingRecommendationEnabled = useIsSellerFeatureEnabled(Features.PackingRecommendations);
  const currentOptimizationFactor = useSellerSetting(SellerSettings.packingRecommendationOptimizationFactor) ?? DEFAULT_OPTIMIZATION_FACTOR;
  const context = useFulfillmentPlannerContext();
  const [showResults, setShowResults] = useState(
    () => isCurrentlyCalculating(fulfillmentPlanId) || getCachedResult(fulfillmentPlanId) !== null || hasError(fulfillmentPlanId),
  );
  const { isActivated: isPackagingExpanded, activate: expandPackaging, deactivate: collapsePackaging } = useToggle(false);

  const [shouldLoadProducts, setShouldLoadProducts] = useState(
    () => isCurrentlyCalculating(fulfillmentPlanId) || getCachedResult(fulfillmentPlanId) !== null || hasError(fulfillmentPlanId),
  );
  const [isCalculationPending, setIsCalculationPending] = useState(false);
  const [validationIssueType, setValidationIssueType] = useState<BlockingValidationErrorType | null>(null);

  const { order, availablePackages, sellerDimensionUnit, sellerWeightUnit, hasMissingProducts } = useOrderData(
    fulfillmentPlanId,
    shouldLoadProducts,
  );

  const handleCalculationComplete = useCallback(
    (completedResult: JobResultResponse) => {
      clearCalculating(fulfillmentPlanId);
      cacheResult(fulfillmentPlanId, completedResult);
      setShowResults(true);
      const isMultiBox = completedResult.isMultiBox ?? false;
      const boxCount = isMultiBox ? (completedResult.boxes?.length ?? 1) : 1;
      const packageName = isMultiBox ? (completedResult.boxes?.[0]?.box.name ?? '') : (completedResult.optimalBox?.name ?? '');
      trackPackingRecommendationShown(fulfillmentPlanId, packageName, isMultiBox, boxCount);
    },
    [fulfillmentPlanId],
  );

  const handleCalculationError = useCallback(
    (err: PackingError) => {
      clearCalculating(fulfillmentPlanId);
      markError(fulfillmentPlanId, err);
      setShowResults(true);
      trackPackingRecommendationFailed(fulfillmentPlanId, err.code);
    },
    [fulfillmentPlanId],
  );

  const {
    isCalculating,
    jobId,
    result: hookResult,
    error,
    startCalculation,
    resumePolling,
    reset,
  } = usePackingCalculation({
    onComplete: handleCalculationComplete,
    onError: handleCalculationError,
  });

  const result = hookResult ?? getCachedResult(fulfillmentPlanId);
  const effectiveError = error ?? (!error && hasError(fulfillmentPlanId) ? getPersistedError(fulfillmentPlanId) : null);

  const packingRecommendationGuard = usePackingRecommendationGuard(fulfillmentPlanId);

  const { handleApply, isApplied, clearApplied } = useApplyRecommendation(
    result,
    availablePackages,
    fulfillmentPlanId,
    sellerDimensionUnit,
    sellerWeightUnit,
    order.items as OrderItem[],
    packingRecommendationGuard,
    currentPackagesOverride,
  );

  const prevIsAppliedRef = useRef(false);
  const isRerunningRef = useRef(false);
  useEffect(() => {
    if (!prevIsAppliedRef.current && isApplied) {
      clearCachedResult(fulfillmentPlanId);
      setShowResults(false);
      reset();
    } else if (prevIsAppliedRef.current && !isApplied) {
      collapsePackaging();
      if (isRerunningRef.current) {
        isRerunningRef.current = false;
      } else if (!(hideButton && isMultiPackageDrawerOpen(fulfillmentPlanId))) {
        clearCachedResult(fulfillmentPlanId);
      }
    }
    prevIsAppliedRef.current = isApplied;
  }, [isApplied, reset, fulfillmentPlanId, collapsePackaging, hideButton]);

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) as
    | (FulfillmentPlanPackingRecommendation & { packages?: Package[]; items?: OrderItem[] })
    | undefined;
  const persistedStatus = getTerminalPackingRecommendationStatus(fulfillmentPlan);
  const persistedFailureReason = fulfillmentPlan?.packingRecommendationFailureReason ?? null;
  const packages = currentPackagesOverride ?? fulfillmentPlan?.packages ?? emptyArray;
  const packagesSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const signature = JSON.stringify(
      packages.map((p: { packageId?: string; weight?: { value?: number }; dimensions?: Record<string, number> }) => ({
        id: p.packageId,
        w: p.weight?.value,
        d: p.dimensions,
      })),
    );
    if (packagesSignatureRef.current === null) {
      packagesSignatureRef.current = signature;
      return;
    }
    if (signature !== packagesSignatureRef.current && showResults && !isApplied && !isCalculating) {
      clearCachedResult(fulfillmentPlanId);
      setShowResults(false);
      setValidationIssueType(null);
      reset();
    }
    packagesSignatureRef.current = signature;
  }, [packages, showResults, isApplied, isCalculating, fulfillmentPlanId, reset]);

  const isCalculatingRef = useRef(isCalculating);
  isCalculatingRef.current = isCalculating;

  const availablePackagesSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const signature = JSON.stringify(availablePackages.map((pkg) => pkg.packageId ?? pkg.name));
    if (availablePackagesSignatureRef.current === null) {
      availablePackagesSignatureRef.current = signature;
      return;
    }
    if (signature !== availablePackagesSignatureRef.current && effectiveError) {
      clearError(fulfillmentPlanId);
      setShowResults(false);
      reset();
    }
    availablePackagesSignatureRef.current = signature;
  }, [availablePackages, effectiveError, fulfillmentPlanId, reset]);

  const currentOptimizationFactorRef = useRef(currentOptimizationFactor);
  currentOptimizationFactorRef.current = currentOptimizationFactor;

  const doCalculate = useCallback(
    (isRerun = false) => {
      if (isCalculatingRef.current) return;
      isRerunningRef.current = isRerun;

      setValidationIssueType(null);

      if (!isRerun) {
        trackPackingRecommendationRequested(fulfillmentPlanId, currentOptimizationFactor);
      }

      const { validation, request } = validateAndTransform(order, availablePackages, {
        sourceDimensionUnit: sellerDimensionUnit,
        sourceWeightUnit: sellerWeightUnit,
        optimizationFactor: currentOptimizationFactor,
      });

      if (!request) {
        const blockingIssueType = BLOCKING_VALIDATION_PRIORITY.find((issueType) => validation.errors.some((err) => err.type === issueType));
        trackPackingRecommendationFailed(fulfillmentPlanId, blockingIssueType ?? validation.errors[0]?.type ?? 'VALIDATION_FAILED');

        if (blockingIssueType) {
          clearCalculating(fulfillmentPlanId);
          setValidationIssueType(blockingIssueType);
          setShowResults(true);
        } else {
          NotificationService.showNotification({
            content: intl.formatMessage(messages.validationFailed),
            notificationType: NOTIFICATION_TYPE_ERROR,
          });
          clearCalculating(fulfillmentPlanId);
        }
        return;
      }

      startCalculation(request);
    },
    [order, availablePackages, sellerDimensionUnit, sellerWeightUnit, currentOptimizationFactor, startCalculation, intl, fulfillmentPlanId],
  );

  const handleCalculate = useCallback(
    (isRerun = false) => {
      setShouldLoadProducts(true);
      if (hasMissingProducts) {
        isRerunningRef.current = isRerun;
        setIsCalculationPending(true);
        return;
      }
      doCalculate(isRerun);
    },
    [doCalculate, hasMissingProducts],
  );

  useEffect(() => {
    if (isCalculationPending && !hasMissingProducts) {
      setIsCalculationPending(false);
      doCalculate(isRerunningRef.current);
    }
  }, [isCalculationPending, hasMissingProducts, doCalculate]);

  const handleBroadcastTrigger = useCallback(
    (isRerun: boolean) => {
      if (hideButton && isMultiPackageDrawerOpen(fulfillmentPlanId)) return;
      handleCalculate(isRerun);
    },
    [handleCalculate, hideButton, fulfillmentPlanId],
  );

  useOnTriggerCalculation(fulfillmentPlanId, handleBroadcastTrigger);

  useEffect(() => {
    if (jobId && isCalculating) {
      const existing = getActiveJob(fulfillmentPlanId);
      const startTime = existing?.startTime ?? Date.now();
      setActiveJob(fulfillmentPlanId, jobId, startTime);
    }
  }, [fulfillmentPlanId, jobId, isCalculating]);

  const resumePollingRef = useRef(resumePolling);
  resumePollingRef.current = resumePolling;
  useEffect(() => {
    if (hideButton && isMultiPackageDrawerOpen(fulfillmentPlanId)) return;
    if (isCurrentlyCalculating(fulfillmentPlanId)) {
      const activeJob = getActiveJob(fulfillmentPlanId);
      if (activeJob) {
        trackPackingRecommendationRequested(fulfillmentPlanId, currentOptimizationFactorRef.current);
        resumePollingRef.current(activeJob.jobId, activeJob.startTime);
      }
    }
  }, [fulfillmentPlanId, hideButton]);

  const handleCalculateClick = useCallback(() => {
    handleCalculate(false);
  }, [handleCalculate]);

  const handleCancel = useCallback(() => {
    clearCalculating(fulfillmentPlanId);
    clearCachedResult(fulfillmentPlanId);
    clearError(fulfillmentPlanId);
    setShowResults(false);
    setIsCalculationPending(false);
    setShouldLoadProducts(false);
    setValidationIssueType(null);
    reset();
  }, [fulfillmentPlanId, reset]);

  const handleResultCleared = useCallback(() => {
    setShowResults(false);
    setValidationIssueType(null);
    reset();
  }, [reset]);

  useOnResultCleared(fulfillmentPlanId, handleResultCleared);

  const handleResultReady = useCallback(() => {
    if (!isApplied) {
      setShowResults(true);
    }
  }, [isApplied]);

  useOnResultReady(fulfillmentPlanId, handleResultReady);

  const handleCardCancel = useCallback(() => {
    const hasRealRecommendation = Boolean(result) && !effectiveError && !isCalculating;
    if (!hasRealRecommendation) {
      handleCancel();
      return;
    }

    const isMultiBox = result?.isMultiBox ?? false;
    const packageName = isMultiBox ? (result?.boxes?.[0]?.box.name ?? '') : (result?.optimalBox?.name ?? '');
    packingRecommendationGuard.guardOverride(() => {
      trackPackingRecommendationRejected(fulfillmentPlanId, packageName);
      handleCancel();
    }, packageName);
  }, [result, effectiveError, isCalculating, packingRecommendationGuard, handleCancel, fulfillmentPlanId]);

  const handleDismissApplied = useCallback(() => {
    const packageName = getAppliedState(fulfillmentPlanId)?.packageName ?? '';
    packingRecommendationGuard.guardOverride(() => {
      clearApplied();
      setShouldLoadProducts(false);
    }, packageName);
  }, [fulfillmentPlanId, packingRecommendationGuard, clearApplied]);

  const handleManageSettings = useCallback(() => {
    navigate(getPackagesCustomSettingsPath());
  }, [navigate]);

  const handleRerun = useCallback(() => {
    trackPackingRecommendationRerunClicked(fulfillmentPlanId, currentOptimizationFactor);
    triggerCalculation(fulfillmentPlanId, true);
  }, [fulfillmentPlanId, currentOptimizationFactor]);

  const handleRetry = useCallback(() => {
    trackPackingRecommendationRetryClicked(fulfillmentPlanId, currentOptimizationFactor);
    triggerCalculation(fulfillmentPlanId, true);
  }, [fulfillmentPlanId, currentOptimizationFactor]);

  if (!isPackingRecommendationEnabled) {
    return null;
  }

  const { onPackageChange } = context ?? {};
  const hasContextCallbacks = onPackageChange && typeof onPackageChange === 'function';
  const canShow = hasContextCallbacks && order.items?.length > 0;

  if (!canShow) {
    return null;
  }

  if (isApplied) {
    const { boxes: appliedBoxes = [] } = getAppliedState(fulfillmentPlanId) ?? {};
    const hasAppliedBoxes = appliedBoxes.length > 0;

    return (
      <>
        <FlexRow className={classnames(cardStyle.card, cardStyle.cardAnimated, cardStyle.cardMargin)} gap={Gap.Space8}>
          <div className={cardStyle.cardIconWrapper}>
            <FontAwesomeIcon className={cardStyle.cardIcon} icon={faSparklesSolid} />
          </div>
          <FlexColumn className={cardStyle.cardBody} gap={Gap.Space8}>
            <FlexRow className={cardStyle.cardHeadline}>
              <Text className={cardStyle.cardTitle} textType={TextType.Label} value={messages.recommendationApplied} />
              <FlexRow className={cardStyle.cardHeadlineActions} gap={Gap.Space8}>
                <Button onClick={handleRerun} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
                  <FormattedMessage {...messages.rerun} />
                </Button>
                {hasAppliedBoxes && (
                  <UnstyledButton
                    aria-expanded={isPackagingExpanded}
                    aria-label={intl.formatMessage(isPackagingExpanded ? messages.collapsePackaging : messages.expandPackaging)}
                    className={cardStyle.chevronButton}
                    onClick={isPackagingExpanded ? collapsePackaging : expandPackaging}
                  >
                    <FontAwesomeIcon icon={isPackagingExpanded ? faChevronUp : faChevronDown} />
                  </UnstyledButton>
                )}
              </FlexRow>
            </FlexRow>
            {isPackagingExpanded && hasAppliedBoxes && (
              <>
                <PackingPackageList boxes={appliedBoxes} sellerDimensionUnit={sellerDimensionUnit} />
                <FlexRow gap={Gap.Space8}>
                  <Button
                    leftSection={<FontAwesomeIcon icon={faArrowRotateRight} />}
                    onClick={handleDismissApplied}
                    size='condensed'
                    style={CONDENSED_BUTTON_FONT_FIX}
                    variant='tertiary'
                  >
                    <FormattedMessage {...messages.clear} />
                  </Button>
                  <Button onClick={handleManageSettings} rightSection={<FontAwesomeIcon icon={faArrowRight} />} variant='hyperlink'>
                    <FormattedMessage {...messages.manageSettings} />
                  </Button>
                </FlexRow>
              </>
            )}
          </FlexColumn>
        </FlexRow>
        <PackingRecommendationWarningModal
          onCancel={packingRecommendationGuard.dismissWarning}
          onProceed={packingRecommendationGuard.confirmWarningAndProceed}
          opened={packingRecommendationGuard.isWarningModalOpen}
        />
        <PackingRecommendationOverrideReasonModal
          onCancel={packingRecommendationGuard.dismissOverrideReason}
          onSubmit={packingRecommendationGuard.submitOverrideReasonAndProceed}
          opened={packingRecommendationGuard.isOverrideReasonModalOpen}
        />
      </>
    );
  }

  if (
    persistedStatus &&
    persistedStatus !== PackingRecommendationStatus.Applied &&
    !isCalculating &&
    !showResults &&
    !(hideButton && isMultiPackageDrawerOpen(fulfillmentPlanId))
  ) {
    const isFailed = persistedStatus === PackingRecommendationStatus.Failed;
    const isStale = persistedStatus === PackingRecommendationStatus.Stale;
    const statusTitleMessage = STATUS_TITLE_MESSAGES[persistedStatus as keyof typeof STATUS_TITLE_MESSAGES];
    const failureReasonMessage = persistedFailureReason ? FAILURE_REASON_MESSAGES[persistedFailureReason] : null;

    return (
      <FlexRow className={classnames(cardStyle.card, cardStyle.cardAnimated, cardStyle.cardMargin)} gap={Gap.Space8}>
        <div className={cardStyle.cardIconWrapper}>
          <FontAwesomeIcon className={cardStyle.cardIcon} icon={faSparklesSolid} />
        </div>
        <FlexColumn className={cardStyle.cardBody} gap={Gap.Space8}>
          <FlexRow className={cardStyle.cardHeadline}>
            <Text className={cardStyle.cardTitle} textType={TextType.Label} value={<FormattedMessage {...statusTitleMessage} />} />
            <FlexRow className={cardStyle.cardHeadlineActions} gap={Gap.Space8}>
              <Button onClick={handleRerun} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
                <FormattedMessage {...messages.rerun} />
              </Button>
            </FlexRow>
          </FlexRow>
          {isFailed && failureReasonMessage && (
            <PackingInlineBanner
              status={persistedFailureReason === PackingRecommendationFailureReason.NoItems ? 'warning' : 'error'}
              description={<FormattedMessage {...failureReasonMessage} />}
            />
          )}
          {isStale && <PackingInlineBanner status='warning' description={<FormattedMessage {...messages.statusCardStaleTitle} />} />}
        </FlexColumn>
      </FlexRow>
    );
  }

  if (!isCalculating && !showResults) {
    if (hideButton) {
      return null;
    }
    return (
      <FlexRow className={cardStyle.card} gap={Gap.Space8}>
        <div className={cardStyle.cardIconWrapper}>
          <FontAwesomeIcon className={cardStyle.cardIcon} icon={faSparklesSolid} />
        </div>
        <FlexColumn className={cardStyle.cardBody}>
          <FlexRow className={cardStyle.cardHeadline}>
            <Text className={cardStyle.cardTitle} textType={TextType.Label} value={messages.panelTitle} />
            <Button onClick={handleCalculateClick} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
              <FormattedMessage {...messages.run} />
            </Button>
          </FlexRow>
        </FlexColumn>
      </FlexRow>
    );
  }

  if (validationIssueType) {
    return <PackingValidationWarning issueType={validationIssueType} onRetry={handleRetry} />;
  }

  return (
    <>
      <PackingResultCard
        error={effectiveError}
        isCalculating={isCalculating}
        onApply={handleApply}
        onCancel={handleCardCancel}
        onRerun={handleRerun}
        onRetry={handleRetry}
        result={result}
        sellerDimensionUnit={sellerDimensionUnit}
      />
      <PackingRecommendationWarningModal
        onCancel={packingRecommendationGuard.dismissWarning}
        onProceed={packingRecommendationGuard.confirmWarningAndProceed}
        opened={packingRecommendationGuard.isWarningModalOpen}
      />
      <PackingRecommendationOverrideReasonModal
        onCancel={packingRecommendationGuard.dismissOverrideReason}
        onSubmit={packingRecommendationGuard.submitOverrideReasonAndProceed}
        opened={packingRecommendationGuard.isOverrideReasonModalOpen}
      />
    </>
  );
};
